/**
 * Monthly free premium games — update each month (keep in sync with be/src/config/monthlyFreeGames.ts).
 *
 * Rules:
 * - First 2 games in each pack are always free.
 * - Monthly free = the 2 premium games in pack positions 3–4 (reorder `*_GAMES` in games.ts to match).
 * - Badge copy: `common.premiumBadge.freeThisMonth` ("Free this Month").
 *
 * Rotation history (do not re-feature the same pair two months in a row when avoidable):
 *
 * | Month        | Blitz                                      | Team                                           | Family                                      | Party                                    |
 * |--------------|--------------------------------------------|------------------------------------------------|---------------------------------------------|------------------------------------------|
 * | July 2026    | speed-challenge, picture-perfect           | business-brain-teasers, its-funny              | wonder-world, what-am-i                     | are-you-drunk, rapid-fire                |
 * | August 2026  | close-or-not, fact-or-myth                 | before-or-after, team-pair-matching            | family-pair-matching, how-many              | would-you-rather, stoned-yet             |
 * | Sept 2026    | almost-genius, pair-matching               | clue-in, its-funny                             | wonder-world, what-am-i                     | kinky-or-not, play-it-loud               |
 *
 * Family has only 4 premium → Sept revisits July. Team only had Clue in! left → + It's funny! (July).
 * When rotating: set MONTHLY_FREE_PERIOD_LABEL, replace IDs below, move those games to
 * positions 3–4 in fe/src/config/games.ts, update e2e/premium tests.
 */
import { BLITZ_GAMES, PARTY_GAMES, TEAM_GAMES, FAMILY_GAMES } from './games';
import type { GameConfig } from './types';
import { config } from './index';

export type GamePack = 'blitz' | 'team' | 'family' | 'party';

/** Shown in the lobby banner, e.g. "September 2026". */
export const MONTHLY_FREE_PERIOD_LABEL = 'September 2026';

export const MONTHLY_FREE_GAME_IDS: Record<GamePack, readonly string[]> = {
  blitz: ['almost-genius', 'pair-matching'],
  team: ['clue-in', 'its-funny'],
  family: ['wonder-world', 'what-am-i'],
  party: ['kinky-or-not', 'play-it-loud'],
};

const PACK_GAMES: Record<GamePack, GameConfig[]> = {
  blitz: BLITZ_GAMES,
  team: TEAM_GAMES,
  family: FAMILY_GAMES,
  party: PARTY_GAMES,
};

export function isMonthlyFreeGame(gameId: string, pack?: GamePack): boolean {
  const packKey = pack ?? (config.appId as GamePack);
  const ids = MONTHLY_FREE_GAME_IDS[packKey];
  return ids?.includes(gameId) ?? false;
}

export function getMonthlyFreeGames(pack?: GamePack): GameConfig[] {
  const packKey = pack ?? (config.appId as GamePack);
  const ids = MONTHLY_FREE_GAME_IDS[packKey] ?? [];
  const games = PACK_GAMES[packKey] ?? [];
  return ids
    .map((id) => games.find((g) => g.id === id))
    .filter((g): g is GameConfig => g != null);
}
